import React, { useState, useEffect } from 'react';
import { useOffline } from '../context/OfflineContext';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { 
  Wifi, 
  WifiOff, 
  RefreshCw, 
  Download,
  Smartphone,
  Check,
  X
} from 'lucide-react';
import { toast } from 'sonner';

export default function OfflineIndicator() {
  const { isOnline, isSyncing, pendingCount, syncData, lastSyncTime } = useOffline();
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
    }

    // Listen for install prompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for successful install
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setDeferredPrompt(null);
      toast.success('App installed successfully!');
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
    setShowInstallPrompt(false);
  };

  const handleManualSync = () => {
    if (isOnline) {
      syncData();
    } else {
      toast.error('Cannot sync while offline');
    }
  };

  return (
    <>
      {/* Floating status indicator */}
      <div className="fixed bottom-4 left-4 z-50 flex flex-col gap-2">
        {/* Offline badge */}
        {!isOnline && (
          <div className="flex items-center gap-2 bg-amber-100 text-amber-800 px-3 py-2 rounded-full shadow-lg animate-pulse">
            <WifiOff className="h-4 w-4" />
            <span className="text-sm font-medium">Offline Mode</span>
          </div>
        )}

        {/* Pending sync badge */}
        {pendingCount > 0 && (
          <div 
            className="flex items-center gap-2 bg-blue-100 text-blue-800 px-3 py-2 rounded-full shadow-lg cursor-pointer hover:bg-blue-200 transition-colors"
            onClick={handleManualSync}
          >
            {isSyncing ? (
              <RefreshCw className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span className="text-sm font-medium">
              {isSyncing ? 'Syncing...' : `${pendingCount} pending`}
            </span>
          </div>
        )}

        {/* Install app button */}
        {deferredPrompt && !isInstalled && (
          <Button
            size="sm"
            className="shadow-lg"
            onClick={() => setShowInstallPrompt(true)}
          >
            <Download className="h-4 w-4 mr-2" />
            Install App
          </Button>
        )}
      </div>

      {/* Install prompt dialog */}
      <Dialog open={showInstallPrompt} onOpenChange={setShowInstallPrompt}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Smartphone className="h-5 w-5" />
              Install QA Builder Pro
            </DialogTitle>
            <DialogDescription>
              Install the app on your device for the best experience
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Check className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">Works Offline</p>
                  <p className="text-sm text-muted-foreground">
                    Create and edit forms without internet
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Check className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">Quick Access</p>
                  <p className="text-sm text-muted-foreground">
                    Launch directly from home screen
                  </p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <Check className="h-4 w-4 text-green-600" />
                </div>
                <div>
                  <p className="font-medium">Auto Sync</p>
                  <p className="text-sm text-muted-foreground">
                    Changes sync automatically when online
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => setShowInstallPrompt(false)}>
              Maybe Later
            </Button>
            <Button className="flex-1 bg-primary" onClick={handleInstall}>
              <Download className="h-4 w-4 mr-2" />
              Install Now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Header status component for inline display
export function OnlineStatus() {
  const { isOnline, isSyncing, pendingCount } = useOffline();

  return (
    <div className="flex items-center gap-2">
      {isOnline ? (
        <Badge variant="outline" className="text-green-600 border-green-200 bg-green-50">
          <Wifi className="h-3 w-3 mr-1" />
          Online
        </Badge>
      ) : (
        <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50">
          <WifiOff className="h-3 w-3 mr-1" />
          Offline
        </Badge>
      )}
      
      {pendingCount > 0 && (
        <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50">
          {isSyncing ? (
            <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
          ) : (
            <RefreshCw className="h-3 w-3 mr-1" />
          )}
          {pendingCount} to sync
        </Badge>
      )}
    </div>
  );
}
